import React, { useCallback, useEffect, useState, useRef } from "react";
import { withRouter } from "react-router";
import ReactGA from "react-ga4";
import Tabs, { TabData } from "src/shared/components/Tabs";

import { SearchIconAllOrg } from "src/assets/search-icon.js";
import { useRedux } from "src/shared/hooks";
import { IRootState } from "src/shared/types";
import styles from "./index.module.scss";
import { changeTimeTabAppWide, UserRoles } from "src/store/app";
import MetricGroups from "src/shared/components/MetricGroups";
import { IActionFactory } from "src/store";
import { SearchTarget } from "src/shared/types";
import { REFRESH_ICON_ANIMATION } from "src/shared/config";
import { TabNames } from "src/api/common";
import {
  clearAllOrgMetrics,
  fetchAllOrgMetrics,
  AllOrgMetricsState,
  FetchAllOrgMetricsPayload,
} from "src/store/allOrg";
import { ICategorialData } from "src/shared/models/AllOrgMetricsModel";
import { Button } from "antd";
import { useEffectOnPropsChange, safeGet } from "src/shared/utils";
import styled from "@emotion/styled";
import colors from "src/styles/variables.scss";
import { animated, useSpring } from "react-spring";
import { SyncOutlined } from "src/icons";

const CALL_DELAY = 1500;
const RefreshBadge = styled(animated.button)`
  border: none;
  display: flex;
  align-items: center;
  border-radius: 16px;
  background: transparent;
  color: ${colors.paleYellow};
  font-size: 12px;
  width: fit-content;
  line-height: 23px;
  margin-top: 3px;
  position: relative;
  outline: none;
  opacity: 0.6;
  > span {
    margin-right: 5px;
  }
  padding: 0 10px;
`;

const IconHolder = styled.div`
  font-size: 16px;
  margin-right: 5px;
  margin-left: 4px;
  line-height: 13px;
`;

const InfoBanner = styled.section`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #00000028;
  padding-left: 10px;
  border-radius: 4px;
`;

const InfoText = styled.p`
  color: ${colors.anomalyIndicator};
  padding: 0 !important;
  margin: 0;
`;

interface IAllOrgMetricsProps {
  onSearchClick: () => void;
  onShowPermissions: () => void;
  setTargetSearch: (value: SearchTarget) => void;
  isPulledUp: boolean;
  showSearch: boolean;
  handleRefresh: () => void;
}

type MappedActionProps = {
  fetchAllOrgMetrics(payload?: FetchAllOrgMetricsPayload);
  clearAllOrgMetrics: IActionFactory<symbol, unknown>;
  changeTimeTabAppWide: IActionFactory<symbol, string>;
};
interface IState {
  allOrgMetrics: AllOrgMetricsState;
  selectedTab: string;
  chartsDataPending: boolean;
  userRoles: UserRoles;
}

const AllOrg = (props) => {
  const mappedProps = useRedux<IRootState, IState, MappedActionProps, {}>(
    (state) => ({
      allOrgMetrics: state.home.allOrgMetrics,
      selectedTab: state.app.selectedTab,
      chartsDataPending: state.home.allOrgMetrics.chartsDataPending,
      userRoles: state.app.userRoles.payload,
    }),
    {
      clearAllOrgMetrics,
      fetchAllOrgMetrics,
      changeTimeTabAppWide,
    }
  );

  const handleChangeTab = useCallback((tabData: TabData) => {
    mappedProps.changeTimeTabAppWide(tabData.key);
    mappedProps.clearAllOrgMetrics();
    // mappedProps.fetchAllOrgMetrics();
  }, []);

  useEffectOnPropsChange(() => {
    mappedProps.fetchAllOrgMetrics();
  }, [mappedProps.selectedTab]);

  useEffectOnPropsChange(() => {
    let anomalyCount = 0,
      failureCount = 0;
    const categorialData = safeGet(mappedProps, "allOrgMetrics.metrics.payload.categorialData", []);
    const groups = Array.isArray(categorialData) ? categorialData : [];
    groups.forEach((group) => {
      const tiles = Array.isArray(group?.tiles) ? group.tiles : [];
      tiles.forEach((tile) => {
        if (!tile.successful) {
          failureCount++;
          ReactGA.event({
            category: "Couldn't fetch Metric",
            label: `L1b:${group.id}: ${tile.label}`,
            action: "Couldn't fetch on L1b",
          });
        } else if (tile.isAnomalous) {
          anomalyCount++;
          ReactGA.event({
            category: tile.isAnomalous === 2 ? "Anomalous: Threshold Breach" : "Anomalous Data",
            label: `L1b:${group.id}: ${tile.label}`,
            action: "Anomalous on L1b",
          });
        } else if (tile.isStale) {
          anomalyCount++;
          ReactGA.event({
            category: "Stale Metric",
            label: `L1b:${group.id}: ${tile.label}`,
            action: "Stale on L1b",
          });
        }
      });
    });

    ReactGA.event({
      category: "Count of Anomalous Metrics L1 b",
      label: `L1b`,
      action: "Anomalous on L1b",
      value: anomalyCount,
    });

    ReactGA.event({
      category: "Count of couldn't fetch L1 b",
      label: `L1b`,
      action: "Couldn't fetch on L1b",
      value: failureCount,
    });
  }, [safeGet(mappedProps, "allOrgMetrics.metrics.payload.lastUpdatedTimeStamp", null)]);

  const onSearchClick = useCallback((evt) => {
    evt.preventDefault();
    props.setTargetSearch(SearchTarget.ALLORGMETRICS);
    props.onSearchClick();
  }, []);

  const showDetails = useCallback((id, name) => {
    props.history.push(`/details/metric/${id}`);
    ReactGA.event({
      category: "Metric Clicked on L1b",
      label: `Metric: ${name} on L1b`,
      action: "Click from L1b",
    });
  }, []);

  const [isRefreshClicked, setIsRefreshClicked] = useState(0);

  const onRefresh = useCallback(() => {
    if (isRefreshClicked) return;
    setIsRefreshClicked(Date.now());
    props.handleRefresh();
  }, [isRefreshClicked, props.handleRefresh]);

  useEffect(() => {
    if (
      isRefreshClicked &&
      Date.now() - isRefreshClicked > CALL_DELAY &&
      safeGet(mappedProps, "chartsDataPending", false) === false
    ) {
      setIsRefreshClicked(0);
    }
  }, [safeGet(mappedProps, "chartsDataPending", false), isRefreshClicked]);

  const badgeRef = useRef();
  const applyBadgeRef = (target) => {
    if (!badgeRef.current) {
      badgeRef.current = target && target.getBoundingClientRect();
    }
  };

  // const permittedMetricIds = ((mappedProps.userRoles.payload && mappedProps.userRoles.payload.metrics) || []).map(
  //   ({ id }) => id
  // );
  const rawCategories = safeGet(mappedProps, "allOrgMetrics.metrics.payload.categorialData", []);
  let categories: ICategorialData[] = Array.isArray(rawCategories) ? rawCategories : [];

  // if (mappedProps.isAdminStatus === "SUCCESS" && !mappedProps.isAdmin) {
  //   categories = categories
  //     .map((category: ICategorialData) => ({
  //       ...category,
  //       tiles: category.tiles.filter(({ id }) => permittedMetricIds.includes(id))
  //     }))
  //     .filter(category => category.tiles.length > 0);
  // }

  const syncIcon = (
    <IconHolder>
      <SyncOutlined />
    </IconHolder>
  );

  // ********** Animations Start ************

  const iconStyles = useSpring(REFRESH_ICON_ANIMATION);

  const refreshBadgeInitialState = {
    opacity: 0.6,
    translateX: 0,
    translateY: 0,
    backgroundColor: "transparent",
    color: colors.paleYellow,
    duration: 400,
  };
  const [refreshBadgeStyles, refreshBadgeApi] = useSpring(() => refreshBadgeInitialState);

  const slideDownInitialState = { paddingTop: 0, duration: 300 };
  const [slideDownStyles, slideDownApi] = useSpring(() => slideDownInitialState);

  useEffect(() => {
    if (isRefreshClicked) {
      slideDownApi({
        paddingTop: 20,
      });
      refreshBadgeApi({
        translateX:
          window.innerWidth / 2 - safeGet(badgeRef, "current.width", 0) / 2 - safeGet(badgeRef, "current.left", 0),
        translateY: 20,
        opacity: 1,
        backgroundColor: colors.canaryHighlight,
        color: colors.l2Background,
      });
    } else {
      slideDownApi(slideDownInitialState);
      refreshBadgeApi(refreshBadgeInitialState);
    }
  }, [isRefreshClicked]);

  // ********** Animations End ************

  return (
    <div className={styles["all-org-container"]}>
      <div className={styles["all-org-header"]}>
        <div className={styles["all-org-subhead"]}>
          <div className={styles["all-org-title"]}>ALL ORG METRICS</div>
          {/* <div className={styles["all-org-subtitle"]}>
              {props.isPulledUp
                ? "Pull down to view your focus metrics"
                : "Pull up to view all org metrics"}
            </div> */}
          {!props.isPulledUp && <div className={styles["all-org-subtitle"]}>Pull up to view all org metrics</div>}
        </div>

        {props.isPulledUp && (
          <div className={styles["all-org-sync"]} onClick={onRefresh}>
            <SyncOutlined />
          </div>
        )}
        {props.isPulledUp && (
          <div className={styles["all-org-search"]} onClick={onSearchClick}>
            {!props.showSearch && (
              <>
                <SearchIconAllOrg />
              </>
            )}
          </div>
        )}
        {props.isPulledUp && !!isRefreshClicked && (
          <RefreshBadge style={refreshBadgeStyles} onClick={onRefresh} ref={applyBadgeRef}>
            <span>updating</span>
            <animated.div style={iconStyles}>{syncIcon}</animated.div>
          </RefreshBadge>
        )}
      </div>
      <animated.div className={styles["all-org-scroller"]} style={slideDownStyles}>
        <Tabs
          tabs={TabNames.map((tabName) => ({ title: tabName, key: tabName }))}
          page={mappedProps.selectedTab}
          onChange={handleChangeTab}
        >
          {TabNames.map((tabName) => (
            <div key={tabName} ref={props.innerRef} id="all-org-content" className={styles["all-org-content"]}>
              {!mappedProps.userRoles.hasFullAccess && (
                <InfoBanner>
                  <InfoText>Your role has limited access</InfoText>
                  <Button type="link" onClick={props.onShowPermissions}>
                    Learn More
                  </Button>
                </InfoBanner>
              )}

              {mappedProps.allOrgMetrics.metrics.success &&
                categories.map((categoricalData: ICategorialData) => (
                  <div className={styles["metric-group-container"]} key={categoricalData.id}>
                    <MetricGroups
                      failure={safeGet(mappedProps, "allOrgMetrics.metrics.failure")}
                      tiles={Array.isArray(categoricalData.tiles) ? categoricalData.tiles : []}
                      label={categoricalData.label}
                      showDetails={showDetails}
                      selectedTab={mappedProps.selectedTab}
                    />
                  </div>
                ))}
              {mappedProps.allOrgMetrics.metrics.failure && (
                <div style={{ textAlign: "center", padding: "40px 0", opacity: 0.7 }}>
                  <p style={{ marginBottom: 12 }}>Failed to load All Org metrics</p>
                  <Button size="small" onClick={onRefresh}>Retry</Button>
                </div>
              )}
              <div style={{ background: "#33333d", padding: 10, borderRadius: 4, textAlign: "center" }}>
                For any issue, query or feedback <br />
                Please reach out to us at
                <a target="_blank" rel="noreferrer" href="https://swiggy.slack.com/archives/anobis-support">
                  {" #anobis-support "}
                </a>
                or
                <a
                  href="mailto:data.products@swiggy.in?
                        &subject=Anobis related issue"
                >
                  {" data.products@swiggy.in"}
                </a>
              </div>
            </div>
          ))}
        </Tabs>
      </animated.div>
    </div>
  );
};
const AllOrgWithRouter = withRouter(AllOrg);
export default React.forwardRef<any, IAllOrgMetricsProps>((props, ref) => (
  <AllOrgWithRouter innerRef={ref} {...props} />
));
